/**
 * Light / dark, as `DESIGN.md` §3 defines it: "a token swap on
 * `:root[data-theme='dark']`, not a second set of components".
 *
 * Three states, not two, and the third is the important one. A viewer who has made
 * no choice gets **no** `data-theme` attribute at all, so `tokens.css`'s
 * `prefers-color-scheme` block decides and the operating system wins. Stamping
 * `light` by default would override an OS-level dark preference on first load, which
 * is exactly the thing people who set that preference are trying to avoid.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';

/** `null` = no explicit choice; the OS preference applies. */
export type ThemeChoice = Theme | null;

export interface ThemeValue {
  readonly theme: ThemeChoice;
  readonly setTheme: (theme: ThemeChoice) => void;
}

const STORAGE_KEY = 'wp.theme';

const ThemeContext = createContext<ThemeValue | null>(null);

function readStored(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'light' || raw === 'dark' ? raw : null;
  } catch {
    return null;
  }
}

export function ThemeProvider({
  children,
  initialTheme,
}: {
  readonly children: ReactNode;
  readonly initialTheme?: ThemeChoice;
}): ReactNode {
  const [theme, setThemeState] = useState<ThemeChoice>(() => initialTheme ?? readStored());

  useEffect(() => {
    const root = document.documentElement;
    if (theme === null) root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setThemeState(next);
    try {
      if (next === null) window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* a preference that cannot be remembered is not an error worth surfacing */
    }
  }, []);

  const value = useMemo<ThemeValue>(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error('useTheme was called outside <ThemeProvider>. Wrap the tree in one.');
  }
  return value;
}
